import Collapse from 'bootstrap/js/dist/collapse'
import Tooltip from 'bootstrap/js/dist/tooltip'
import Popover from 'bootstrap/js/dist/popover'

function scrollTrigger(selector, options = {rootMargin: '0px'}) {
  let els = document.querySelectorAll(selector)
  els = Array.from(els)
  els.forEach(el => {
    addObserver(el, options)
  })
}

function addObserver(el, options) {
  let observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      }
    });
  }, options);

  observer.observe(el);
}

let lastScrollTop = window.pageYOffset || document.documentElement?.scrollTop || 0;
let isPageLoaded = false;

['wheel', 'touchmove', 'keydown', 'mousedown'].forEach(evt => {
  window.addEventListener(evt, () => {
    isPageLoaded = true;
  }, { passive: true, once: true });
});

function checkScrollPosition(isInitial = false) {
  const body = document.body;
  if (!body) {
    return;
  }

  const toTop = document.querySelectorAll('.back-to-top');
  const currentScrollTop = window.pageYOffset || document.documentElement?.scrollTop || body.scrollTop || 0;

  if (currentScrollTop > 10) {
    body.classList.add('scroll');
  } else {
    body.classList.remove('scroll');
  }

  if (!isInitial) {
    if (currentScrollTop > lastScrollTop && currentScrollTop > 10) {
      body.classList.add('scroll-down');
      body.classList.remove('scroll-up');
    } else if (currentScrollTop < lastScrollTop) {
      body.classList.add('scroll-up');
      body.classList.remove('scroll-down');
    }

    if (currentScrollTop <= 10) {
      body.classList.remove('scroll-up', 'scroll-down');
    }
  }

  lastScrollTop = Math.max(0, currentScrollTop);

  if (currentScrollTop > 300) {
    toTop.forEach(function (el) {
      el.classList.add('show');
    });
  } else {
    toTop.forEach(function (el) {
      el.classList.remove('show');
    });
  }
}

function getShareData(element) {
  return {
    url: element?.dataset.shareUrl || window.location.href,
    title: element?.dataset.shareTitle || document.title,
    text: element?.dataset.shareText || '',
  };
}

function initVideos() {
  function loadLazyVideo(video) {
    if (!video.classList.contains('lazy')) {
      return;
    }

    for (const videoSource of video.children) {
      if (videoSource.tagName === 'SOURCE' && videoSource.dataset.src) {
        videoSource.src = videoSource.dataset.src;
      }
    }

    video.load();
    video.classList.remove('lazy');
  }

  function shouldAutoplayVideo(video) {
    return video.dataset.autoplay === '1' || video.hasAttribute('autoplay');
  }

  function loadVisibleVideos(container = document) {
    container.querySelectorAll('.isotope-item:not([style*="display: none"]) video.lazy').forEach(function (video) {
      const item = video.closest('.isotope-item');

      if (!item || item.offsetParent === null) {
        video.pause();
        return;
      }

      loadLazyVideo(video);

      if (shouldAutoplayVideo(video)) {
        video.play().catch(() => {
          // ignore play errors
        });
      }
    });
  }

  window.addEventListener('drive:load-visible-videos', function (event) {
    loadVisibleVideos(event.detail && event.detail.container ? event.detail.container : document);
  });

  function initSwiperSlideChangeVideoLoading() {
    document.querySelectorAll('.swiper').forEach((swiperEl) => {
      const swiper = swiperEl.swiper;

      if (!swiper || typeof swiper.on !== 'function') {
        return;
      }

      if (swiperEl.dataset.videoSlideChangeBound === '1') {
        return;
      }

      let swiperInView = false;

      const handleSlideChange = () => {
        if (!swiperInView) {
          return;
        }

        swiperEl.querySelectorAll('.swiper-slide-visible video').forEach((video) => {
          loadLazyVideo(video);

          if (shouldAutoplayVideo(video)) {
            video.play().catch(() => {
              // ignore play errors
            });
          }
        });
      };

      const swiperObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === swiperEl) {
            swiperInView = entry.isIntersecting;

            if (swiperInView) {
              handleSlideChange();
            }
          }
        });
      }, {
        threshold: 0.1
      });

      swiperObserver.observe(swiperEl);

      swiper.on('slideChange', handleSlideChange);
      swiper.on('destroy', () => {
        swiperObserver.disconnect();
      });
      swiperEl.dataset.videoSlideChangeBound = '1';
    });
  }

  const allVideos = document.querySelectorAll('video.lazy, video[data-autoplay="1"], video[autoplay]');

  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        const video = entry.target;

        if (entry.isIntersecting) {
          const style = window.getComputedStyle(video);
          if (style.visibility === 'hidden' || style.opacity === '0') {
            video.pause();
            return;
          }

          const slide = video.closest('.ce-slide, .swiper-slide');
          if (slide) {
            if (slide.classList.contains('swiper-slide')) {
              if (!slide.classList.contains('swiper-slide-visible')) {
                video.pause();
                return;
              }
            } else if (!slide.classList.contains('active')) {
              video.pause();
              return;
            }
          }

          loadLazyVideo(video);

          if (shouldAutoplayVideo(video)) {
            video.play().catch(() => {
              // ignore play errors
            });
          }

        } else {
          video.pause();
        }
      });
    }, {
      threshold: 0.1
    });

    allVideos.forEach(function(video) {
      videoObserver.observe(video);
    });

    initSwiperSlideChangeVideoLoading();
  } else {
    allVideos.forEach(function(video) {
      loadLazyVideo(video);

      if (shouldAutoplayVideo(video)) {
        video.play().catch(() => {
          // ignore play errors
        });
      }
    });
  }
}

function initButtons() {
  const toTopBtn = document.querySelectorAll('[data-toggle="to-top"]');
  const videoBtnList = document.querySelectorAll('[data-toggle="video"]');
  const fullscreenBtnList = document.querySelectorAll('[data-toggle="fullscreen"]');
  const shareBtnList = document.querySelectorAll('[data-toggle="share"]');
  const backButtonList = document.querySelectorAll('[data-toggle="back"]');

  toTopBtn.forEach(function (el) {
    el.addEventListener('click', () => {
      window.scrollTo({top: 0, behavior: 'smooth'});

      const target = document.querySelector('main') || document.body;

      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      target.addEventListener('blur', () => {
        target.removeAttribute('tabindex');
      }, { once: true });
    });
  });

  videoBtnList.forEach(function (el) {
    const video = el.dataset.target ? document.querySelector(el.dataset.target) : null;

    if (!video) {
      return;
    }

    el.addEventListener('click', () => {
      if (video) {
        if (!el.classList.contains('btn-play-corner')) {
          video.controls = true;
        }

        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    });

    const videoWrapper = video.closest('.video');

    video.addEventListener('play', () => {
      if (videoWrapper) {
        videoWrapper.classList.add('active');
      }

      el.classList.add('active');
      el.classList.remove('show');
      el.title = el.dataset.labelPause || 'Pause';
    });

    video.addEventListener('pause', () => {
      if (videoWrapper) {
        videoWrapper.classList.remove('active');
      }

      el.classList.remove('active');
      el.classList.add('show');
      el.title = el.dataset.labelPlay || 'Play';
    });
  });

  fullscreenBtnList.forEach(function (el) {
    const target = document.querySelector(el.dataset.target);

    target.addEventListener('dblclick', () => {
      if (target.requestFullscreen) {
        target.requestFullscreen();
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
      } else if (target.mozRequestFullScreen) {
        target.mozRequestFullScreen();
      } else if (target.msRequestFullscreen) {
        target.msRequestFullscreen();
      }
    });

    el.addEventListener('click', () => {
      if (target) {
        if (target.requestFullscreen) {
          target.requestFullscreen();
        } else if (target.webkitRequestFullscreen) {
          target.webkitRequestFullscreen();
        } else if (target.mozRequestFullScreen) {
          target.mozRequestFullScreen();
        } else if (target.msRequestFullscreen) {
          target.msRequestFullscreen();
        }
      }
    });
  });

  shareBtnList.forEach(function (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      const { url, title, text } = getShareData(shareBtn);

      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
        } catch (error) {
          console.log('Share cancelled or failed:', error);
        }
      } else {
        console.log('Web Share API not supported, use fallback buttons');
      }
    });
  });

  backButtonList.forEach(function (button) {
    button.addEventListener('click', function (event) {
      // Use browser history if available, otherwise follow the fallback href
      if (window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    });
  });
}

function initNavbar() {
  const header = document.getElementById('header');
  const navbar = document.getElementById('main-nav');

  if (!navbar) {
    return;
  }

  const collapseTriggerList = navbar.querySelectorAll(':scope > .nav-children');

  collapseTriggerList.forEach(function (el) {
    const collapseEl = el.querySelector('.navbar-sub-collapse.level-1');
    const subCollapseList = collapseEl.querySelectorAll('.navbar-sub-collapse');

    if (collapseEl) {
      collapseEl.addEventListener('show.bs.collapse', () => {
        el.classList.add('open');
        navbar.classList.add('open');
      });

      collapseEl.addEventListener('hide.bs.collapse', event => {
        if (event.target === event.currentTarget) {
          el.classList.remove('open');
          navbar.classList.remove('open');
        }
      });

      subCollapseList.forEach(function (subCollapseEl) {
        subCollapseEl.addEventListener('show.bs.collapse', () => {
          subCollapseEl.parentElement.classList.add('open');
        });

        subCollapseEl.addEventListener('hide.bs.collapse', event => {
          if (event.target === event.currentTarget) {
            subCollapseEl.parentElement.classList.remove('open');
          }
        });
      });
    }
  });

  header.querySelectorAll('.navbar-collapse').forEach(function (collapse) {
    collapse.addEventListener('show.bs.collapse', event => {
      if (event.target === event.currentTarget) {
        header.classList.add('open');
      }
    })
    collapse.addEventListener('hide.bs.collapse', event => {
      if (event.target === event.currentTarget) {
        header.classList.remove('open');
      }
    })
  });

  document.addEventListener('keydown', (e) => {
    const activeCollapseList = document.querySelectorAll('.collapse.show');
    const activePopoverList = document.querySelectorAll('[data-bs-toggle="popover"][aria-describedby]');

    if (e.key === 'Escape') {
      activeCollapseList.forEach(function (collapseEl) {
        const collapse = Collapse.getOrCreateInstance(collapseEl, {
          toggle: false
        });
        collapse.hide();
      });

      activePopoverList.forEach(function (pin) {
        const popover = Popover.getInstance(pin);

        if (popover) {
          popover.hide();
          pin.focus();
        }
      });
    }
  });

  window.addEventListener('click', function (e) {
    if (navbar.contains(e.target)) {
      // Clicked in box
    } else {
      const collapseElementList = navbar.querySelectorAll('.collapse.level-1.show');

      [...collapseElementList].map(function (collapseEl) {
        const collapse = Collapse.getOrCreateInstance(collapseEl, {
          toggle: false
        });

        collapse.hide();
      });
    }
  });
}

function initTooltips() {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl));
}

function initCollapse() {
  const accordionTabsElementList = document.querySelectorAll('.accordion-tabs');
  const cardOverlayElementList = document.querySelectorAll('.card-menu .card-img-overlay');

  [...accordionTabsElementList].map(function (accordionEl) {
    const accordionCollapseElementList = accordionEl.querySelectorAll('.collapse');
    let opening = false;

    [...accordionCollapseElementList].map(function (collapseEl) {
      if (window.innerWidth < 992) {
        accordionEl.style.removeProperty('height');
      } else {
        if (collapseEl.classList.contains('show') && collapseEl.classList.contains('accordion-parent')) {
          accordionEl.style.setProperty('height', `${Math.max(collapseEl.offsetHeight, accordionEl.offsetHeight)}px`);
        }
      }

      collapseEl.addEventListener('show.bs.collapse', event => {
        opening = true;
        accordionEl.classList.add('open');

        if (window.innerWidth < 992) {
          accordionEl.style.removeProperty('height');
        } else {
          if (event.target === event.currentTarget) {
            collapseEl.classList.add('d-block', 'invisible');

            if (collapseEl.closest('.collapse.show > .accordion-body')) {
              accordionEl.style.setProperty('height', `${Math.max(collapseEl.closest('.collapse.show > .accordion-body').offsetHeight, accordionEl.offsetHeight)}px`);
            } else {
              accordionEl.style.setProperty('height', `${Math.max(collapseEl.offsetHeight, accordionEl.offsetHeight)}px`);
            }

            collapseEl.classList.remove('d-block', 'invisible');
          }
        }
      });

      collapseEl.addEventListener('shown.bs.collapse', event => {
        if (window.innerWidth >= 992) {
          if (event.target === event.currentTarget) {
            if (collapseEl.closest('.collapse.show > .accordion-body')) {
              accordionEl.style.setProperty('height', `${Math.max(collapseEl.closest('.collapse.show > .accordion-body').offsetHeight, accordionEl.offsetHeight)}px`);
            } else {
              accordionEl.style.setProperty('height', `${Math.max(collapseEl.offsetHeight, accordionEl.offsetHeight)}px`);
            }
          }
        }
      });

      collapseEl.addEventListener('hide.bs.collapse', event => {
        if (event.target === event.currentTarget) {
          if (!opening) {
            event.preventDefault();
            event.stopPropagation();
          } else {
            opening = false;
          }
        }
      });

      collapseEl.addEventListener('hidden.bs.collapse', event => {
        if (collapseEl.closest('.accordion').querySelector('[aria-expanded="true"]') == null) {
          if (accordionEl.querySelector('[aria-expanded="true"]') == null) {
            accordionEl.classList.remove('open');
          }

          if (window.innerWidth < 992) {
            accordionEl.style.removeProperty('height');
          } else {
            if (event.target === event.currentTarget) {
              if (collapseEl.closest('.collapse.show > .accordion-body')) {
                accordionEl.style.setProperty('height', `${collapseEl.closest('.collapse.show > .accordion-body').offsetHeight}px`);
              } else {
                accordionEl.style.setProperty('height', `${accordionEl.offsetHeight}px`);
              }
            }
          }
        }
      });
    });
  });

  [...cardOverlayElementList].map(function (cardOverlayEl) {
    const collapseEl = cardOverlayEl.querySelector('.card-text');

    if (collapseEl) {
      ['mouseenter', 'focusin'].forEach(evt => cardOverlayEl.addEventListener(evt, () => {
        const collapse = Collapse.getOrCreateInstance(collapseEl, {
          toggle: false
        });
        collapse.show();
        cardOverlayEl.closest('.card').classList.add('card-overlay-active');
      }));

      ['mouseleave', 'focusout'].forEach(evt => cardOverlayEl.addEventListener(evt, event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          const collapse = Collapse.getOrCreateInstance(collapseEl, {
            toggle: false
          });
          collapse.hide();
          cardOverlayEl.closest('.card').classList.remove('card-overlay-active');
        }
      }));
    }
  });
}

function initForms() {
  // Fetch all the forms we want to apply custom Bootstrap validation styles to
  const forms = document.querySelectorAll('.needs-validation')

  // Loop over them and prevent submission
  Array.from(forms).forEach(form => {
    form.addEventListener('submit', event => {
      if (!form.checkValidity()) {
        event.preventDefault()
        event.stopPropagation()
      }

      form.classList.add('was-validated')
    }, false)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    checkScrollPosition(true);
  });
} else {
  checkScrollPosition(true);
}

function initMap() {
  const map = document.getElementById("svg-map");

  if (!map) {
    return;
  }

  map.querySelectorAll('[data-bs-toggle="popover"]').forEach(function (popoverTriggerEl) {
    new Popover(popoverTriggerEl, {
      trigger: 'click',
      html: true,
      container: popoverTriggerEl.closest('.map-pin'),
      popperConfig: {
        placement: 'bottom-end', modifiers: [{
          name: 'flip', options: {
            fallbackPlacements: ['bottom-start', 'top-end', 'top-start'],
          },
        }, {
          name: 'offset', options: {
            offset: ({placement, reference}) => {
              if (placement === 'top-start' || placement === 'bottom-start') {
                return [reference.height / 2, reference.height / -2];
              } else {
                return [reference.height / -2, reference.height / -2];
              }
            },
          },
        },],
      }
    });
  });

  document.addEventListener('click', function (e) {
    map.querySelectorAll('[data-bs-toggle="popover"][aria-describedby]').forEach(function (pin) {
      // Trigger and popover both live inside the .map-pin container
      if (pin.closest('.map-pin').contains(e.target)) {
        return;
      }

      const popover = Popover.getInstance(pin);

      if (popover) {
        popover.hide();
      }
    });
  });
}

window.addEventListener('load', function () {
  checkScrollPosition(true);
  initButtons();
  initNavbar();
  initVideos();
  initTooltips();
  initCollapse();
  initForms();
  initMap();
  setTimeout(() => {
    isPageLoaded = true;
  }, 50);
});

window.onscroll = function () {
  checkScrollPosition(!isPageLoaded);
};
